/* eslint-disable @typescript-eslint/no-explicit-any */
import {Injectable} from '@angular/core';
import {FeaturesApplyInsertService} from '../../features-apply-insert.service';
import {NgEventBus} from 'ng-event-bus';
import {SpatialSequenceService} from '../../../../common/service/spatial-sequence.service';
import {SpatialUtilService} from '../../spatial-util.service';
import {AngleUtilService} from './angle-util.service';
import {tap} from 'rxjs';
import {AppConfigService} from '../../../../common/service/app-config.service';

@Injectable({
    providedIn: 'root'
})
export class AngleCreationService {
    title = 'AngleCreationService';

    ANGLE_GENERATION_METHOD = {
        IMPORT: {comment: 'NLR calculated on import'},
        CONSTRUCTION_TOOLS: {comment: 'Generated in portal'}
    };

    constructor(
        private appConfigService: AppConfigService,
        private angleUtilService: AngleUtilService,
        private eventBus: NgEventBus,
        private featuresApplyInsertService: FeaturesApplyInsertService,
        private spatialSequenceService: SpatialSequenceService,
        private spatialUtilService: SpatialUtilService
    ) {}

    createMissingCadastralAngles(angles: any[], points: any[], lines: any[], generatedMethodInfo: any, includeInterests: boolean) {
        console.debug(`${this.title} - createMissingCadastralAngles`);

        const cadastralPoints = this.angleUtilService.removeNotApplicablePoints(points);
        const cadastralLines = this.angleUtilService.removeNonCadastralLines(lines, includeInterests);

        if (cadastralPoints.length > 0 && cadastralLines.length > 0) {
            const linesAssociatedToPoint = this.angleUtilService.getRelatedLines(cadastralPoints, cadastralLines);
            const vectors = this.buildVectors(linesAssociatedToPoint, cadastralPoints);
            const sortedVectors = this.sortVectors(vectors);

            let anglesFromVectors = this.buildAnglesFromVectors(sortedVectors, angles, lines, includeInterests);

            // Only round angles if angles already exist as we already create missing angles from vectors and the last angle is calculated accordingly
            if (angles.length > 0) {
                this.roundAnglesByPointTo360(cadastralPoints, anglesFromVectors);
            }

            anglesFromVectors = this.removeExistingAngles(anglesFromVectors);

            return this.angleUtilService.completeAnglesCreation(
                anglesFromVectors,
                cadastralPoints,
                linesAssociatedToPoint,
                true,
                generatedMethodInfo.comment
            );
        }

        return [];
    }

    createMissingInterestAngles(angles: any[], points: any[], lines: any[], generatedMethodInfo: any) {
        console.debug(`${this.title} - createMissingInterestAngles`);

        const cadastralPoints = this.angleUtilService.removeNotApplicablePoints(points);
        const cadastralLines = this.angleUtilService.removeNonCadastralLines(lines, true);

        if (cadastralPoints.length > 0 && cadastralLines.length > 0) {
            const createdAnglesPerPolygon = [];
            const interestLinesArray = this.applyInterestLineCondition(cadastralLines);
            interestLinesArray.forEach((linesArray: any[]) => {
                const linesAssociatedToPoint = this.angleUtilService.getRelatedLines(cadastralPoints, linesArray);
                const vectors = this.buildVectors(linesAssociatedToPoint, cadastralPoints);
                const sortedVectors = this.sortVectors(vectors);

                const anglesFromVectors = this.buildAnglesFromVectors(sortedVectors, angles, lines, true);

                const result = this.angleUtilService.completeAnglesCreation(
                    anglesFromVectors,
                    cadastralPoints,
                    linesAssociatedToPoint,
                    true,
                    generatedMethodInfo.comment
                );

                createdAnglesPerPolygon.push(...result);
            });

            // check if there are redundant/duplicate angles created around each selected potint
            const checkedAngles = [];
            this.removeRedundantAnglesAroundPoints(cadastralPoints, createdAnglesPerPolygon, checkedAngles);

            return this.removeExistingAngles(checkedAngles);
        }

        return [];
    }

    insertAngles(angles: any[]) {
        console.debug(`${this.title} - insertAngles`);

        const sequence = this.spatialSequenceService.getAngleSequence(angles.length);
        angles.forEach((angle: any, angleIndex: number) => (angle.attributes.id = sequence[angleIndex]));

        return this.featuresApplyInsertService.insertFeaturesByType(this.spatialUtilService.getAngleMapLayer(), angles).pipe(
            tap((createdAngles: any) => {
                console.debug(`${this.title} - insertAngles - angles have been successfully created!`);

                this.eventBus.cast(this.appConfigService.getDesignProperties().EVENTS.FEATURES_CREATED, {
                    features: createdAngles,
                    featureLayer: this.spatialUtilService.getAngleMapLayer()
                });
            })
        );
    }

    private applyInterestLineCondition(lines: any[]) {
        const output = [];

        const interestLines = lines.filter((line: any) => this.angleUtilService.isInterestLine(line));
        if (interestLines.length > 1) {
            const interestLineGroups = [];

            // are they linked to an interest polygon
            this.spatialUtilService
                .getPolygons()
                .filter((polygon: any) => polygon.attributes.lineids)
                .forEach((polygon: any) =>
                    interestLineGroups.push(
                        interestLines.filter((interestLine: any) => polygon.attributes.lineids.includes(`[${interestLine.attributes.id}]`))
                    )
                );

            if (interestLineGroups.length > 0) {
                const interestsNotLinkedToPolygon = interestLines.filter(
                    (interestLine: any) =>
                        !interestLineGroups.some((group: any[]) => group.some((item: any) => item.attributes.id === interestLine.attributes.id))
                );

                if (interestsNotLinkedToPolygon.length > 0) {
                    interestLineGroups.push(interestsNotLinkedToPolygon);
                }

                interestLineGroups.forEach((group: any) => {
                    const newLineGroup = [];

                    lines.forEach((line: any) => {
                        group.forEach((item: any) => {
                            if (this.angleUtilService.isInterestLine(line) && item.attributes.id === line.attributes.id) {
                                newLineGroup.push(line);
                            }
                        });
                        if (!this.angleUtilService.isInterestLine(line)) {
                            newLineGroup.push(line);
                        }
                    });

                    output.push(newLineGroup);
                });
            } else {
                output.push(lines);
            }
        } else {
            output.push(lines);
        }

        return output;
    }

    private buildVectors(lines: any[], points: any[]) {
        console.debug(`${this.title} - buildVectors`);

        const output = [];

        lines.forEach((line: any) => {
            const bearing = this.angleUtilService.getLineBearing(line);

            const fromPointId = parseInt(line.attributes.frompointid);
            const toPointId = parseInt(line.attributes.topointid);

            if (points.some((point: any) => point.attributes.id === fromPointId)) {
                output[fromPointId] = output[fromPointId] ? output[fromPointId] : {};
                output[fromPointId][`${bearing}`] = line.attributes.id;
            }

            if (points.some((point: any) => point.attributes.id === toPointId)) {
                const bearingCalc = (bearing + 180) % 360;

                output[toPointId] = output[toPointId] ? output[toPointId] : {};
                output[toPointId][`${bearingCalc}`] = line.attributes.id;
            }
        });

        return output;
    }

    private sortVectors(vectors: any[]) {
        console.debug(`${this.title} - sortVectors`);

        const output = {};

        vectors.forEach((vector: any, index: number) => {
            const bearings = [];
            const lines = [];

            for (const [bearing, lineId] of Object.entries(vector) as any[]) {
                bearings.push(parseFloat(bearing));
                lines.push(lineId);
            }

            for (let x = 0; x < bearings.length - 1; x++) {
                for (let y = x + 1; y < bearings.length; y++) {
                    if (bearings[x] > bearings[y]) {
                        // Swap bearing
                        const previousBearing = bearings[y];
                        bearings[y] = bearings[x];
                        bearings[x] = previousBearing;

                        // Swap line
                        const previousLine = lines[y];
                        lines[y] = lines[x];
                        lines[x] = previousLine;
                    }
                }
            }

            for (let z = 0; z < bearings.length; z++) {
                output[index] = output[index] ? output[index] : [];
                output[index].push({bearing: bearings[z], lineId: lines[z]});
            }
        });

        return output;
    }

    private getById(lines: any[], id: number) {
        return lines.find(e => e.attributes.id === id);
    }

    private buildAnglesFromVectors(vectors: any, angles: any[], lines: any[], checkInterest: boolean) {
        console.debug(`${this.title} - buildAnglesFromVectors`);

        const output = [];
        let previousBearing = 0;
        let previousLine = 0;
        let firstLine = undefined;

        for (const pointId in vectors) {
            const pointIdInt = parseInt(pointId);
            const vector = vectors[pointIdInt];
            let loop = 0;
            let sumOfAngles = '';

            const existingAnglesByPoint = angles.filter(existingAngle => existingAngle.attributes.pointid === pointIdInt);

            vector.forEach((v: any) => {
                const bearing = v.bearing;
                const lineId = v.lineId;

                if (loop === 0) {
                    previousBearing = bearing;
                    previousLine = lineId;
                    firstLine = lineId;
                } else {
                    let angle = existingAnglesByPoint.find(
                        e => e.attributes.anglefromlineid === previousLine && e.attributes.angletolineid === lineId
                    );

                    if (!angle) {
                        const angleValue = this.angleUtilService.subtractBearings(bearing, previousBearing);

                        angle = {
                            attributes: {
                                pointid: pointIdInt,
                                anglefromlineid: previousLine,
                                angletolineid: lineId,
                                internalangle: this.angleUtilService.ddToDms(angleValue),
                                anglerotation: (previousBearing + angleValue / 2.0) % 360
                            }
                        };
                    }

                    if (
                        !checkInterest ||
                        lines.some(
                            l => (l.attributes.id === previousLine || l.attributes.id === lineId) && this.angleUtilService.isInterestLine(l)
                        ) ||
                        this.angleUtilService.isInterestLine(this.getById(lines, previousLine)) ||
                        this.angleUtilService.isInterestLine(this.getById(lines, lineId))
                    ) {
                        output.push(angle);
                    }

                    sumOfAngles = this.addAngles(sumOfAngles, angle.attributes.internalangle);
                    previousBearing = bearing;
                    previousLine = lineId;
                }

                ++loop;
            });

            if (loop > 1) {
                let angle = existingAnglesByPoint.find(
                    existingAngle => existingAngle.attributes.anglefromlineid === previousLine && existingAngle.attributes.angletolineid === firstLine
                );

                if (!angle) {
                    const angleValueDms = this.angleUtilService.subtractAngleFrom360(sumOfAngles);

                    angle = {
                        attributes: {
                            pointid: pointIdInt,
                            anglefromlineid: previousLine,
                            angletolineid: firstLine,
                            internalangle: angleValueDms,
                            anglerotation: (previousBearing + this.angleUtilService.dmsToDd(angleValueDms) / 2.0) % 360
                        }
                    };
                }

                if (
                    !checkInterest ||
                    lines.some(l => (l.attributes.id === previousLine || l.attributes.id === firstLine) && this.angleUtilService.isInterestLine(l)) ||
                    this.angleUtilService.isInterestLine(this.getById(lines, previousLine)) ||
                    this.angleUtilService.isInterestLine(this.getById(lines, firstLine))
                ) {
                    output.push(angle);
                }
            }
        }

        return output;
    }

    private roundAnglesByPointTo360(points: any[], angles: any[]) {
        console.debug(`${this.title} - roundAnglesByPointTo360`);

        points.forEach((point: any) => {
            const anglesByPoint = angles.filter(angle => point.attributes.id === angle.attributes.pointid);

            let sumOfAngles = '';

            const missingAnglesFromVectors = this.removeExistingAngles(anglesByPoint);

            if (missingAnglesFromVectors.length > 0) {
                const lastMissingAngle = missingAnglesFromVectors[missingAnglesFromVectors.length - 1];

                anglesByPoint.forEach((angle: any) => {
                    if (
                        angle.attributes.anglefromlineid !== lastMissingAngle.attributes.anglefromlineid ||
                        angle.attributes.angletolineid !== lastMissingAngle.attributes.angletolineid
                    ) {
                        sumOfAngles = this.addAngles(sumOfAngles, angle.attributes.internalangle);
                    }
                });

                lastMissingAngle.attributes.internalangle = this.angleUtilService.subtractAngleFrom360(sumOfAngles);
            }
        });
    }

    private addAngles(sumOfAngles: any, angle: any) {
        // the angles in are DD:MM:SS format
        console.debug(`${this.title} - addAngles:`, sumOfAngles, angle);

        let angleDegrees = 0;
        let angleMinutes = 0;
        let angleSeconds = 0;
        let sumDegrees = 0;
        let sumMinutes = 0;
        let sumSeconds = 0;

        if (`${angle}`.includes(':')) {
            const angleParts = angle.split(':');
            angleDegrees = parseInt(angleParts[0].toString());
            angleMinutes = parseInt(angleParts[1].toString());
            angleSeconds = parseInt(angleParts[2].toString());
        } else {
            return this.angleUtilService.formattedAngle(sumDegrees, sumMinutes, sumSeconds);
        }

        if (`${sumOfAngles}`.includes(':')) {
            const sumOfAnglesParts = sumOfAngles.split(':');
            sumDegrees = parseInt(sumOfAnglesParts[0].toString());
            sumMinutes = parseInt(sumOfAnglesParts[1].toString());
            sumSeconds = parseInt(sumOfAnglesParts[2].toString());

            sumDegrees = sumDegrees + angleDegrees;
            sumMinutes = sumMinutes + angleMinutes;
            sumSeconds = sumSeconds + angleSeconds;

            while (sumSeconds >= 60) {
                sumSeconds = sumSeconds - 60;
                sumMinutes = sumMinutes + 1;
            }

            while (sumMinutes >= 60) {
                sumMinutes = sumMinutes - 60;
                sumDegrees = sumDegrees + 1;
            }

            return this.angleUtilService.formattedAngle(sumDegrees, sumMinutes, sumSeconds);
        } else {
            return this.angleUtilService.formattedAngle(angleDegrees, angleMinutes, angleSeconds);
        }
    }

    private removeExistingAngles(anglesByVectors: any[]) {
        return anglesByVectors.filter((v: any) => !v.attributes.id);
    }

    /**
     * Check if redundant / duplicate angles created around each selected point, and remove such angles.
     * @param cadastralPoints The selected cadastralPoints
     * @param createdAnglesPerPolygon created angles
     * @param output resulting angles for the selected cadastral points
     */
    private removeRedundantAnglesAroundPoints(cadastralPoints: any[], createdAnglesPerPolygon: any[], output: any[]) {
        console.debug(`${this.title} - removeRedundantAnglesAroundPoints`);

        cadastralPoints.forEach((cadastralPoint: any) => {
            const anglesAroundPoint = createdAnglesPerPolygon
                .filter((angle: any) => angle.attributes.pointid === cadastralPoint.attributes.id)
                .sort((angle1: any, angle2: any) => angle1.attributes.anglefromlineid - angle2.attributes.anglefromlineid);

            // if there are multiple angles with the same fromlineid, select the one with the smallest internal angle within the same fromlineid group
            const anglesReducedWithFromLine = anglesAroundPoint.map((angle, i, a) => {
                const duplicateAngles = a.filter((v: any) => v.attributes.anglefromlineid === angle.attributes.anglefromlineid);
                if (duplicateAngles.length > 1) {
                    duplicateAngles.sort((a, b) => this.sortByInternalAngle(a, b));
                }
                return duplicateAngles[0];
            });

            // if there are multiple angles with the same tolineid, select the one with the smallest internal angle within the same tolineid group
            const anglesReduced = anglesReducedWithFromLine.map((angle, i, a) => {
                const duplicateAngles = a.filter((v: any) => v.attributes.angletolineid === angle.attributes.angletolineid);
                if (duplicateAngles.length > 1) {
                    duplicateAngles.sort((a, b) => this.sortByInternalAngle(a, b));
                }
                return duplicateAngles[0];
            });

            // remove duplicate angles
            anglesReduced.forEach((angleReduced: any) => {
                const hasDuplicate = output.some(
                    (angle: any) =>
                        angle.attributes.anglefromlineid === angleReduced.attributes.anglefromlineid &&
                        angle.attributes.angletolineid === angleReduced.attributes.angletolineid &&
                        angle.attributes.internalangle === angleReduced.attributes.internalangle
                );
                if (!hasDuplicate) {
                    output.push(angleReduced);
                }
            });
        });
    }

    private sortByInternalAngle(angle1: any, angle2: any) {
        const internalAngle1 = this.angleUtilService.dmsToDd(angle1.attributes.internalangle);
        const internalAngle2 = this.angleUtilService.dmsToDd(angle2.attributes.internalangle);
        if (internalAngle1 === internalAngle2) {
            return 0;
        } else if (internalAngle1 > internalAngle2) {
            return 1;
        }
        return -1;
    }
}
